/**
 * sceneLayout.ts — table-first responsive layout math for the desktop scene.
 *
 * Dependency-free (no Phaser import) so it is directly unit testable.
 *
 * WHY THIS EXISTS
 * ---------------
 * The previous desktop layout computed sizes in the wrong direction:
 *
 *   1. reserve ~8% of the height for the hand (handReserve)
 *   2. unit = min(w, h - handReserve) / 19
 *   3. table = min(unit * 19 * 1.15, h * 0.95)     <- two independent caps
 *   4. table anchored to the TOP (boardY = table/2 + 12)
 *
 * That wasted space twice over: the hand reserve shrank the tile unit even
 * though the hand actually renders ON the table's bottom rail (it overlaps by
 * frameOverlap), and the h*0.95 cap then clipped the result again. The table
 * could never exceed 95% of the square, and the leftovers landed as unused
 * band rather than being distributed evenly.
 *
 * This module inverts the derivation — the square is the input, the table is
 * sized directly from it, and the tile unit is derived from the table:
 *
 *   square -> table (fills the square minus one intentional margin)
 *          -> unit  (so the 19-cell grid still sits inside the felt exactly
 *                    as before)
 *          -> felt interior, anchors
 *
 * CRITICAL INVARIANT
 * ------------------
 * Board.drawFelt() computes the drawn table image size with the SAME shared
 * function, drawnTableSize(canvasW, canvasH), from the same canvas
 * dimensions — so the scene's table bounds and the drawn image can never
 * drift apart. (Previously both sides quantized the table to the tile unit
 * via tableSizeForUnit(); that left up to ~GRID_CELLS×scale px of the square
 * unused, which read as a visible backdrop strip around the table.)
 *
 * The tile unit is still derived by integer division of the square (crisp,
 * whole-pixel tiles), so tile size, legal placement, board topology and the
 * board engine's coordinate rules all behave exactly as before — the drawn
 * table simply absorbs the sub-unit remainder (≤2%) instead of leaving it
 * as backdrop, and the tile grid sits marginally deeper inside the felt.
 */

/** Tile grid cells across the board, matching the board engine. */
export const GRID_CELLS = 19;

/**
 * Fraction of the square left as margin on EACH side of the table.
 * 0 per the layout truth (config/layoutSpec.ts): the design places the
 * table edge-to-edge in the 1000x1000 design square. (Was 1% before the
 * layout-editor spec became authoritative.)
 */
export const TABLE_MARGIN_RATIO = 0;

/**
 * Playable green interior as a fraction of the displayed table size.
 *
 * Kept at the pre-refactor effective value (felt = unit*12 while
 * table = unit*19*1.15, i.e. 12 / 21.85) so play-area clamping behaviour is
 * byte-for-byte unchanged by this refactor.
 *
 * MEASURED, FOR A FUTURE DECISION: the actual green felt in
 * Assets/games/dominoes/board/table-green.png spans ~78.5% of the asset
 * (x[166..1342], y[178..1357] of 1500x1500), so there is real headroom above
 * this value. Raising it would enlarge the playable region and let the chain
 * spread further — that is a gameplay-feel change, not a layout fix, so it is
 * deliberately NOT made here.
 */
export const FELT_INTERIOR_RATIO = 12 / (GRID_CELLS * 1.15);

export interface SquareSceneLayout {
  /** Side of the largest square inside the canvas. */
  squareSize: number;
  /** Tile grid unit (px). */
  unit: number;
  /** Displayed table size (px) — square; equals Board.drawFelt()'s tableSize. */
  tableSize: number;
  /** Table centre. */
  tableCX: number;
  tableCY: number;
  /** Table edges. */
  tableLeft: number;
  tableTop: number;
  tableRight: number;
  tableBottom: number;
  /** Playable green interior (square, concentric with the table). */
  feltSize: number;
  feltCX: number;
  feltCY: number;
}

/**
 * The visual footprint of the tile grid (unit × 19 × scale). No longer the
 * drawn table size (see drawnTableSize) — kept as the invariant that the
 * grid footprint always fits INSIDE the drawn table.
 */
export function tableSizeForUnit(unit: number, tableVisualScale: number): number {
  return Math.round(unit * GRID_CELLS * tableVisualScale);
}

/** Largest square that fits a rectangle (integer side). */
export function squareSideFor(canvasW: number, canvasH: number): number {
  if (!Number.isFinite(canvasW) || !Number.isFinite(canvasH)) return 0;
  if (canvasW <= 0 || canvasH <= 0) return 0;
  return Math.floor(Math.min(canvasW, canvasH));
}

/**
 * The DRAWN table size for a canvas: the largest square minus the intentional
 * margin (0 today). SINGLE SOURCE OF TRUTH shared by computeSquareSceneLayout
 * below and Board.drawFelt() — both compute the drawn table from the same
 * canvas dimensions with this function, so the drawn image and the scene's
 * believed table bounds stay in lockstep by construction.
 *
 * Deliberately NOT quantized to the tile unit: the table artwork fills the
 * square edge-to-edge, while the integer tile unit keeps gameplay geometry
 * and tile crispness unchanged.
 */
export function drawnTableSize(canvasW: number, canvasH: number): number {
  const squareSize = squareSideFor(canvasW, canvasH);
  return squareSize - 2 * Math.round(squareSize * TABLE_MARGIN_RATIO);
}

/**
 * Table-first desktop layout. Pure: identical inputs always yield identical
 * output (idempotent across repeated resizes), and nothing here is aware of
 * devicePixelRatio — DPR affects only the physical backing buffer, never
 * these logical coordinates.
 */
export function computeSquareSceneLayout(
  canvasW: number,
  canvasH: number,
  tableVisualScale: number,
  minUnit = 16,
): SquareSceneLayout {
  const squareSize = squareSideFor(canvasW, canvasH);

  // The drawn table fills the square minus one intentional, symmetric margin
  // — the same value Board.drawFelt() computes from the same canvas.
  const usable = drawnTableSize(canvasW, canvasH);

  // Integer tile unit from the table budget (crisp, whole-pixel tiles). The
  // sub-unit remainder is absorbed by the drawn table rather than left as a
  // visible backdrop strip; the grid footprint always fits inside the table.
  const unit = Math.max(minUnit, Math.floor(usable / (GRID_CELLS * tableVisualScale)));
  const tableSize = usable;

  // Centre the table in the canvas. Integer centre keeps tile sprites off
  // sub-pixel boundaries (the pre-existing desktop rounding rationale).
  const tableCX = Math.round(canvasW / 2);
  const tableCY = Math.round(canvasH / 2);
  const half = tableSize / 2;

  const feltSize = Math.round(tableSize * FELT_INTERIOR_RATIO);

  return {
    squareSize,
    unit,
    tableSize,
    tableCX,
    tableCY,
    tableLeft:   tableCX - half,
    tableTop:    tableCY - half,
    tableRight:  tableCX + half,
    tableBottom: tableCY + half,
    feltSize,
    feltCX: tableCX,
    feltCY: tableCY,
  };
}
